import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  reports,
  type ReportDetailInfo,
  type ReportPageKey,
  type ReportQuery,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardHeader } from '@/shared/ui/Card';
import { HelpTip } from '@/shared/ui/FieldLabel';
import { Pagination } from '@/shared/ui/Pagination';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { TEXTO_PENDENTE, alinhaADireita } from './celula';
import { TextoDaCelula } from './Visual';

const POR_PAGINA = 25;

/**
 * "Base - Detalhamento" da pagina: os registros, paginados no servidor, com a
 * exportacao em CSV. A exportacao exige um ano escolhido e a permissao propria; o
 * dado pessoal sai mascarado sem a permissao de ve-lo.
 *
 * A Esteira, o Acumulo e a Aprovacao tem duas tabelas de registros no OnPoint: cada
 * uma e um detalhamento proprio (`detalhe`), com a sua exportacao.
 */
export function Detalhamento({
  pagina,
  filtro,
  detalhe,
}: {
  pagina: ReportPageKey;
  filtro: ReportQuery;
  detalhe?: ReportDetailInfo | undefined;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const [exportando, setExportando] = useState(false);
  const escolhido = detalhe ? { ...filtro, detail: detalhe.key } : filtro;

  const consulta = useQuery({
    queryKey: ['bi', 'detalhe', pagina, escolhido, offset],
    queryFn: ({ signal }) =>
      reports.details(pagina, { ...escolhido, limit: POR_PAGINA, offset }, signal),
  });

  const podeExportar = can('bi.relatorio.export');
  const temPeriodo = (filtro.year?.length ?? 0) > 0;
  const dados = consulta.data;
  const mascarado = dados?.items.some((item) =>
    Object.values(item).some((celula) => celula.masked),
  );

  async function exportar() {
    setExportando(true);
    try {
      const arquivo = await reports.exportDetails(pagina, escolhido);
      const url = URL.createObjectURL(arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.download = detalhe ? `bi-${pagina}-${detalhe.key}.csv` : `bi-${pagina}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Detalhamento exportado.');
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Nao foi possivel exportar.');
    } finally {
      setExportando(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={`Base - ${dados?.title ?? detalhe?.title ?? 'Detalhamento'}`}
        description={
          dados?.reference_date
            ? `O periodo usa: ${dados.reference_date.toLowerCase()}.`
            : 'Registros do periodo e dos filtros.'
        }
        action={
          podeExportar ? (
            <div className="flex flex-col items-end gap-1">
              <Button
                size="sm"
                variant="secondary"
                disabled={!temPeriodo || exportando || dados?.pending}
                onClick={() => void exportar()}
              >
                {exportando ? 'Exportando...' : 'Exportar CSV'}
              </Button>
              {!temPeriodo ? (
                <span className="text-2xs text-ink-500">
                  Escolha ao menos um ano para exportar.
                </span>
              ) : null}
            </div>
          ) : null
        }
      />
      {consulta.isError ? (
        <div className="p-5">
          <Alert tone="danger" title="Nao foi possivel carregar o detalhamento">
            {consulta.error.message}
          </Alert>
        </div>
      ) : consulta.isPending ? (
        <SkeletonRows rows={6} columns={5} />
      ) : dados?.pending ? (
        <div className="p-5">
          <Alert tone="info" title={TEXTO_PENDENTE}>
            {dados.pending_reason}
          </Alert>
        </div>
      ) : dados && dados.items.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-ink-500">
          Nenhum registro no periodo e nos filtros.
        </p>
      ) : dados ? (
        <>
          {mascarado ? (
            <p className="border-b border-line px-5 py-2 text-2xs text-ink-500">
              Dados pessoais mascarados. Ver sem mascara exige permissao propria e fica
              registrado na trilha.
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <caption className="sr-only">{dados.title}</caption>
              <thead>
                <tr className="border-b border-line bg-surface-sunken">
                  {dados.columns.map((coluna) => (
                    <th
                      key={coluna.key}
                      scope="col"
                      className={cn(
                        'whitespace-nowrap px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-ink-500',
                        alinhaADireita(coluna.kind) ? 'text-right' : 'text-left',
                      )}
                    >
                      {coluna.pending ? (
                        <HelpTip label={coluna.label} text={coluna.pending_reason} />
                      ) : (
                        coluna.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.items.map((item, indice) => (
                  <tr key={indice} className="border-b border-line last:border-b-0">
                    {dados.columns.map((coluna) => (
                      <td
                        key={coluna.key}
                        className={cn(
                          'whitespace-nowrap px-3 py-2 tabular-nums text-ink-800',
                          alinhaADireita(coluna.kind) && 'text-right',
                        )}
                      >
                        <TextoDaCelula celula={item[coluna.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            total={dados.total}
            limit={POR_PAGINA}
            offset={offset}
            onChange={setOffset}
          />
        </>
      ) : null}
    </Card>
  );
}
